import { useCallback, useEffect, useMemo, useState } from "react";
import { adminFetch } from "utils/adminFetch";
import { formatExpiringDate } from "utils/formatExpiringDate";

function addDaysPreview(ymd, days) {
  const n = Number(days);
  if (!ymd || !Number.isFinite(n) || n <= 0) return null;
  const d = new Date(`${String(ymd).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + Math.trunc(n));
  return d.toISOString().slice(0, 10);
}

function daysUntilExpiry(ymd) {
  if (!ymd) return null;
  const exp = new Date(`${String(ymd).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(exp.getTime())) return null;
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  return Math.ceil((exp.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
}

function expiryUrgencyClass(ymd) {
  const days = daysUntilExpiry(ymd);
  if (days == null) return "font-semibold text-violet-800 dark:text-violet-200";
  if (days <= 3) {
    return "inline-block rounded-lg bg-red-100 px-2.5 py-1 font-bold text-red-900 ring-1 ring-red-300 dark:bg-red-950/70 dark:text-red-100 dark:ring-red-500/40";
  }
  if (days <= 7) {
    return "inline-block rounded-lg bg-yellow-100 px-2.5 py-1 font-bold text-yellow-900 ring-1 ring-yellow-300 dark:bg-yellow-950/60 dark:text-yellow-100 dark:ring-yellow-500/40";
  }
  return "font-semibold text-violet-800 dark:text-violet-200";
}

function ExpiryDateBadge({ ymd }) {
  return (
    <span className={expiryUrgencyClass(ymd)}>{formatExpiringDate(ymd)}</span>
  );
}

export default function AdminSlaveAccountsTable() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [actionKey, setActionKey] = useState(null);
  const [extendDaysByKey, setExtendDaysByKey] = useState({});

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await adminFetch("/api/admin/duplikium-slaves");
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        const msg = data.message || "Could not load accounts.";
        setError(
          /subscription_path/i.test(msg)
            ? `${msg} — Optional: run backend/sql/subscription_path_columns.sql in Supabase (or refresh after API restart; the list works without that column).`
            : msg,
        );
        setRows([]);
        return;
      }
      setRows(data.rows || []);
    } catch {
      setError("Network error.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  const rowKey = (r) => `${r.clientId}-S${r.slot}`;

  const sortedRows = useMemo(
    () =>
      [...rows]
        .filter((row) => row.tradingAccountId)
        .sort((a, b) => {
          const c = Number(a.clientId) - Number(b.clientId);
          if (c !== 0) return c;
          return Number(a.slot) - Number(b.slot);
        }),
    [rows],
  );

  const clientGroups = useMemo(() => {
    const groups = [];
    for (const row of sortedRows) {
      const last = groups[groups.length - 1];
      if (!last || last.clientId !== row.clientId) {
        groups.push({ clientId: row.clientId, rows: [row] });
      } else {
        last.rows.push(row);
      }
    }
    return groups;
  }, [sortedRows]);

  async function toggleFirstMonthRisk(row) {
    const key = rowKey(row);
    if (actionKey === `risk-${key}`) return;
    setActionKey(`risk-${key}`);
    setMsg("");
    setError("");
    const currentlyEnforced = row.firstMonthRiskEnforced === true;
    const next = !currentlyEnforced;
    // Optimistic UI so Turn off / Turn on feels instant.
    setRows((prev) =>
      prev.map((r) =>
        rowKey(r) === key ? { ...r, firstMonthRiskEnforced: next } : r,
      ),
    );
    try {
      const res = await adminFetch("/api/admin/duplikium-slaves/first-month-risk", {
        method: "POST",
        body: JSON.stringify({
          clientId: row.clientId,
          slot: row.slot,
          enabled: next,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setRows((prev) =>
          prev.map((r) =>
            rowKey(r) === key
              ? { ...r, firstMonthRiskEnforced: currentlyEnforced }
              : r,
          ),
        );
        setError(data.message || "Risk update failed.");
        return;
      }
      setMsg(
        data.message || (next ? "Fixed lot 0.01 active." : "Risk lock off."),
      );
      if (data.warning) {
        setError(data.warning);
      }
      const confirmed =
        data.firstMonthRiskEnforced === true ||
        data.firstMonthRiskEnforced === false
          ? data.firstMonthRiskEnforced === true
          : next;
      setRows((prev) =>
        prev.map((r) =>
          rowKey(r) === key ? { ...r, firstMonthRiskEnforced: confirmed } : r,
        ),
      );
    } catch {
      setRows((prev) =>
        prev.map((r) =>
          rowKey(r) === key
            ? { ...r, firstMonthRiskEnforced: currentlyEnforced }
            : r,
        ),
      );
      setError("Network error.");
    } finally {
      setActionKey(null);
    }
  }

  async function extendExpiry(row) {
    const key = rowKey(row);
    const days = Number(extendDaysByKey[key]);
    if (!Number.isFinite(days) || days < 1) {
      setError("Enter a number of days (1 or more).");
      return;
    }
    if (actionKey) return;
    setActionKey(`ext-${key}`);
    setMsg("");
    setError("");
    try {
      const res = await adminFetch("/api/admin/duplikium-slaves/extend-expiry", {
        method: "POST",
        body: JSON.stringify({
          clientId: row.clientId,
          slot: row.slot,
          days,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setError(data.message || "Could not extend expiry.");
        return;
      }
      setMsg(`+${days} day(s): new expiry ${formatExpiringDate(data.expiringDate)}.`);
      setRows((prev) =>
        prev.map((r) =>
          rowKey(r) === key ? { ...r, expiringDate: data.expiringDate } : r,
        ),
      );
      setExtendDaysByKey((p) => ({ ...p, [key]: "" }));
    } catch {
      setError("Network error.");
    } finally {
      setActionKey(null);
    }
  }

  return (
    <div className="mt-3 rounded-[20px] border border-gray-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-navy-800 sm:p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-navy-700 dark:text-white">
            Client trading accounts
          </h2>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            1st month risk: Fixed lot 0.01 on the copier. Extend expiry adds days to the
            current end date.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void loadRows()}
          disabled={loading}
          className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-semibold text-navy-700 hover:bg-gray-50 disabled:opacity-50 dark:border-white/15 dark:text-white dark:hover:bg-white/10"
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {error ? (
        <p className="mb-3 text-sm font-semibold text-red-600 dark:text-red-400">{error}</p>
      ) : null}
      {msg ? (
        <p className="mb-3 text-sm font-semibold text-emerald-700 dark:text-emerald-300">
          {msg}
        </p>
      ) : null}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[1100px] border-collapse text-left text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-xs font-bold uppercase tracking-wide text-gray-500 dark:border-white/10 dark:text-gray-400">
              <th className="border-r border-gray-200 px-3 py-3 dark:border-white/10">
                Client ID
              </th>
              <th className="border-r border-gray-200 px-3 py-3 dark:border-white/10">
                Site password
              </th>
              <th className="border-r border-gray-200 px-3 py-3 dark:border-white/10">
                Slots
              </th>
              <th className="px-3 py-3">Type</th>
              <th className="px-3 py-3">Account number</th>
              <th className="px-3 py-3">Server</th>
              <th className="px-3 py-3">MT password</th>
              <th className="px-3 py-3 text-center">1st month risk</th>
              <th className="px-3 py-3">Expiring date</th>
              <th className="px-3 py-3">Extend expiring account</th>
              <th className="px-3 py-3">Final expiring date</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={11} className="px-3 py-8 text-center text-gray-500">
                  Loading accounts…
                </td>
              </tr>
            ) : sortedRows.length === 0 ? (
              <tr>
                <td colSpan={11} className="px-3 py-8 text-center text-gray-500">
                  No active slave records in database.
                </td>
              </tr>
            ) : (
              clientGroups.map((group) =>
                group.rows.map((row, rowIndex) => {
                  const key = rowKey(row);
                  const daysRaw = extendDaysByKey[key] ?? "";
                  const preview = addDaysPreview(row.expiringDate, daysRaw);
                  const riskBusy = actionKey === `risk-${key}`;
                  const extBusy = actionKey === `ext-${key}`;
                  const enforced = row.firstMonthRiskEnforced === true;

                  return (
                    <tr
                      key={key}
                      className="border-b border-gray-100 align-middle dark:border-white/5"
                    >
                      {rowIndex === 0 ? (
                        <td
                          rowSpan={group.rows.length}
                          className="border-r border-gray-200 bg-gray-50/80 px-3 py-3 text-center font-bold tabular-nums text-navy-800 dark:border-white/10 dark:bg-navy-900/50 dark:text-white"
                        >
                          <div>{group.clientId}</div>
                          {group.rows[0]?.clientName || group.rows[0]?.email ? (
                            <div className="mt-1 text-[11px] font-normal normal-case text-gray-500 dark:text-gray-400">
                              {group.rows[0]?.clientName || group.rows[0]?.email}
                            </div>
                          ) : null}
                          {group.rows[0]?.username ? (
                            <div className="mt-1 text-[10px] font-normal normal-case text-gray-500 dark:text-gray-400">
                              @{group.rows[0].username}
                            </div>
                          ) : null}
                        </td>
                      ) : null}
                      {rowIndex === 0 ? (
                        <td
                          rowSpan={group.rows.length}
                          className="border-r border-gray-200 px-3 py-3 align-middle dark:border-white/10"
                        >
                          {group.rows[0]?.siteLoginPassword ? (
                            <span className="font-mono text-sm font-semibold text-navy-800 dark:text-gray-100">
                              {group.rows[0].siteLoginPassword}
                            </span>
                          ) : (
                            <span
                              className="text-xs italic text-amber-700 dark:text-amber-300"
                              title="Compte créé avant l'enregistrement admin — réinitialisation mot de passe requise."
                            >
                              Non enregistré
                            </span>
                          )}
                        </td>
                      ) : null}
                      <td className="border-r border-gray-200 px-3 py-3 font-mono font-semibold text-navy-700 underline decoration-navy-400/60 underline-offset-2 dark:border-white/10 dark:text-white">
                        {row.tradingAccountId}
                      </td>
                    <td className="px-3 py-3 font-semibold">{row.accountType || "—"}</td>
                    <td className="px-3 py-3 font-medium tabular-nums">
                      {row.accountNumber || "—"}
                    </td>
                    <td className="max-w-[10rem] px-3 py-3 text-xs font-medium text-navy-800 dark:text-gray-200">
                      {row.server || "—"}
                    </td>
                    <td className="px-3 py-3">
                      {row.tradingPassword ? (
                        <span className="font-mono text-sm font-semibold text-navy-800 dark:text-gray-100">
                          {row.tradingPassword}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-3 align-top">
                      <div className="relative z-10 flex min-h-[4.5rem] min-w-[9.5rem] flex-col items-stretch justify-center gap-1.5">
                        <span
                          className={`inline-flex min-h-[1.75rem] w-full items-center justify-center rounded-lg border-2 px-3 py-1 text-xs font-bold uppercase tracking-wide ${
                            riskBusy
                              ? "border-amber-400 bg-amber-50 text-amber-900"
                              : enforced
                                ? "border-emerald-600 bg-emerald-50 text-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-100"
                                : "border-gray-400 bg-gray-50 text-gray-800 dark:border-white/25 dark:bg-navy-900 dark:text-gray-200"
                          }`}
                          aria-live="polite"
                        >
                          {riskBusy
                            ? "Updating…"
                            : enforced
                              ? "Active"
                              : "Inactive"}
                        </span>
                        <button
                          type="button"
                          disabled={riskBusy}
                          aria-pressed={enforced}
                          onClick={() => void toggleFirstMonthRisk(row)}
                          className="min-h-[2rem] w-full shrink-0 rounded-lg border-2 border-brand-500 bg-brand-500 px-3 py-1.5 text-xs font-bold text-white shadow-sm transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50 dark:border-brand-400 dark:bg-brand-400 dark:hover:bg-brand-300"
                          title={
                            enforced
                              ? "Fixed lot 0.01 on copier — click to release"
                              : "Click to enforce Fixed lot 0.01 on copier"
                          }
                        >
                          {riskBusy
                            ? "Please wait…"
                            : enforced
                              ? "Turn off"
                              : "Turn on"}
                        </button>
                        <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400">
                          Fixed 0.01 lot
                        </span>
                        {!row.duplikiumAccountId ? (
                          <span className="text-center text-[9px] leading-tight text-amber-700 dark:text-amber-300">
                            Copier linked by account name
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <ExpiryDateBadge ymd={row.expiringDate} />
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min={1}
                          max={3650}
                          placeholder="Days"
                          value={daysRaw}
                          onChange={(e) =>
                            setExtendDaysByKey((p) => ({
                              ...p,
                              [key]: e.target.value,
                            }))
                          }
                          className="w-16 rounded-lg border border-gray-300 px-2 py-1.5 text-sm font-semibold dark:border-white/15 dark:bg-navy-900 dark:text-white"
                        />
                        <button
                          type="button"
                          disabled={extBusy}
                          onClick={() => void extendExpiry(row)}
                          className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-brand-600 disabled:opacity-50"
                        >
                          {extBusy ? "…" : "Add days"}
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <ExpiryDateBadge ymd={preview || row.expiringDate} />
                    </td>
                  </tr>
                  );
                }),
              )
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
